import { useMemo } from 'react'
import useGlobal from '@/hooks/useGlobal'
import type {
  DomainType,
  EmailAddressType,
  EmailContactVerificationStatusType,
} from '@/state/entities/marketing/domains/domains.types'
import serviceApi from '@/state/service/service.slice'
import { defaultServicePagination } from '@/connect-types/backend/service'

/** v4 `Badge` variant for a verification status (auto-dark tokens). */
export type VerificationBadgeVariant =
  | 'success'
  | 'warning'
  | 'destructive'
  | 'secondary'

export const badgeVariantFromVerificationStatus = (
  status: EmailContactVerificationStatusType
): VerificationBadgeVariant => {
  switch (status) {
    case 'Success':
      return 'success'
    case 'Failed':
      return 'destructive'
    case 'Pending':
      return 'warning'
    default:
      return 'secondary'
  }
}

export const statusFromContactVerificationStatus = (
  status: EmailContactVerificationStatusType
): string => {
  switch (status) {
    case 'NotStarted':
      return 'Not started'

    default:
      return status
  }
}

export const createDomainFromAddress = (
  emailaddress: EmailAddressType
): DomainType => {
  const domainName = emailaddress.email.split('@').pop() ?? ''
  return {
    id: domainName,
    organisation_id: '',
    dkim_status: 'Success',
    created_at: new Date(),
    updated_at: new Date(),
    name: domainName,
    is_public: true,
    dkim_last_checked_at: new Date(),
    email_address_count: 1,
  }
}

const domainParser = (
  domains: DomainType[] = [],
  addresses: EmailAddressType[] = []
): DomainType[] => {
  const primaryDomains = addresses
    .filter(({ domain_id }) => domain_id === null)
    .map(createDomainFromAddress)

  const newDomains: DomainType[] = [...domains, ...primaryDomains].map(
    (item) => ({
      ...item,
      addresses: addresses.filter(
        ({ domain_id, email }) =>
          domain_id === item.id || item.id === email.split('@').pop()
      ),
    })
  )

  return [
    ...newDomains
      .reduce((a, c) => {
        a.set(c.id, c)
        return a
      }, new Map<string, DomainType>())
      .values(),
  ]
}

export const useFullDomains = () => {
  const { data: addresses, isLoading: isAddressesLoading } = useEmailAddresses()
  const { data: domains, isLoading } = useEmailDomains()

  const cleanedDomains = useMemo(() => {
    const domainList = (domains.data ?? []).filter(
      (item): item is DomainType => item !== undefined
    )
    const addressList = (addresses.data ?? []).filter(
      (item): item is EmailAddressType => item !== undefined
    )
    return domainParser(domainList, addressList)
  }, [addresses.data, domains.data])

  return {
    data: cleanedDomains,
    isLoading: isAddressesLoading || isLoading,
  }
}

export const useEmailDomains = () => {
  const { orgId } = useGlobal()

  const { data = defaultServicePagination, ...rest } =
    serviceApi.useGetEmailDomainsQuery({ orgId })

  return {
    data,
    ...rest,
  }
}

export const useEmailDomain = (domain_id = '') => {
  const { orgId } = useGlobal()

  return serviceApi.useGetEmailDomainQuery(
    { orgId, domain_id },
    { skip: domain_id.length !== 36 }
  )
}

export const useEmailAddresses = (domain_id = '') => {
  const { orgId } = useGlobal()

  const { data = defaultServicePagination, ...rest } =
    serviceApi.useGetEmailAddressesQuery({ orgId, page: 1, domain_id })

  return {
    data,
    ...rest,
  }
}

export const useEmailAddress = (id = '') => {
  const { orgId } = useGlobal()

  return serviceApi.useGetEmailAddressQuery({ orgId, id }, { skip: !id })
}
